<!-- src/components/forms/completed/FatigueAssessmentCompleted.vue -->
<template>
  <div class="space-y-16 animate-fadeIn">
    
    <!-- 1. OPERATIVE IDENTITY -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <UserIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Operative Credentials</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[40px] border border-slate-100 shadow-inner">
        <div class="space-y-6">
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Target Personnel</p>
            <p class="text-lg font-black text-[#0A1630] uppercase">{{ flattenedData.operativeName || 'N/A' }}</p>
          </div>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Designated Role</p>
            <p class="text-sm font-black text-slate-600 uppercase">{{ flattenedData.operativeRole || 'N/A' }}</p>
          </div>
        </div>

        <div class="space-y-6">
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Parent Employer</p>
            <p class="text-sm font-black text-[#0A1630] uppercase">{{ flattenedData.operativeEmployer || 'N/A' }}</p>
          </div>
          <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <p class="micro-label text-slate-400 mb-2">Lead Assessor</p>
            <p class="text-sm font-bold text-slate-500 uppercase italic">{{ flattenedData.assessorName || 'N/A' }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. CONTEXTUAL ANALYSIS -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <MagnifyingGlassIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Contextual Analysis</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-xl flex flex-col justify-center">
          <p class="micro-label text-slate-400 mb-1">Observation Date</p>
          <p class="text-base font-black text-[#0A1630] uppercase">{{ formatDate(flattenedData.observationDate, 'dateOnly') }}</p>
        </div>
        <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-xl flex flex-col justify-center">
          <p class="micro-label text-slate-400 mb-1">Worksite Location</p>
          <p class="text-base font-black text-[#0A1630] uppercase">{{ flattenedData.location || 'N/A' }}</p>
        </div>
        <div class="bg-white p-6 rounded-3xl border border-slate-200 shadow-xl flex flex-col justify-center">
          <p class="micro-label text-[#20FCF1] mb-1">Assessment Driver</p>
          <p class="text-base font-black text-[#0A1630] uppercase">{{ flattenedData.reasonForAssessment || 'Routine' }}</p>
        </div>
      </div>
    </section>

    <!-- 3. SYMPTOMATIC REVIEW -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <EyeIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Symptomatic Review</h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div v-for="(list, title) in signCategories" :key="title" class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
          <p class="micro-label mb-4 text-[#20FCF1]">{{ title }} Indicators</p>
          <div v-if="list && list.length > 0" class="space-y-2">
            <div v-for="item in list" :key="item" class="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 shadow-sm">
              <div class="w-1.5 h-1.5 rounded-full bg-[#20FCF1]"></div>
              <span class="text-[10px] font-black text-[#0A1630] uppercase tracking-tight">{{ item }}</span>
            </div>
          </div>
          <p v-else class="text-[10px] font-bold text-slate-400 uppercase italic">No indicators recorded.</p>
        </div>
      </div>
    </section>

    <!-- 4. FORMAL EVALUATION -->
    <section>
      <div class="flex items-center gap-4 mb-8">
        <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
          <ClipboardDocumentCheckIcon class="h-6 w-6" />
        </div>
        <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Formal Evaluation</h2>
      </div>

      <div class="space-y-8">
        <div class="flex items-center justify-between p-8 bg-[#0A1630] rounded-[32px] shadow-2xl">
          <p class="text-sm font-black text-white uppercase tracking-widest">Is fatigue identified as a critical issue?</p>
          <span :class="flattenedData.isFatigueAnIssue ? 'bg-rose-500 text-white' : 'bg-[#20FCF1] text-[#0A1630]'" class="px-8 py-2 rounded-full text-xs font-black uppercase tracking-widest shadow-lg">
            {{ flattenedData.isFatigueAnIssue ? 'YES - ACTION REQUIRED' : 'NO - CLEAR FOR DUTY' }}
          </span>
        </div>

        <div v-if="flattenedData.mitigatingAction" class="bg-white p-8 rounded-[32px] border border-slate-200 shadow-xl">
          <p class="micro-label text-slate-400 mb-2">Immediate Mitigating Action Taken</p>
          <p class="text-sm text-slate-700 font-medium leading-relaxed">{{ flattenedData.mitigatingAction }}</p>
        </div>
      </div>
    </section>

    <!-- 5. AUTHORIZATION SEAL -->
    <section class="border-t pt-12 flex flex-col items-center">
      <div v-if="flattenedData.signatureDataUrl" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-xl flex flex-col items-center min-w-[320px]">
        <p class="micro-label text-slate-400 mb-6">Assessor Digital Seal</p>
        <img :src="flattenedData.signatureDataUrl" alt="Assessor Signature" class="max-h-24 object-contain grayscale brightness-75">
        <div class="w-full h-px bg-slate-100 my-6"></div>
        <p class="text-[10px] font-black text-[#0A1630] uppercase tracking-widest">{{ flattenedData.assessorName }}</p>
        <p class="text-[9px] font-bold text-slate-400 uppercase mt-1">Verified: {{ formatDate(submissionData.submittedAt) }}</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { UserIcon, MagnifyingGlassIcon, EyeIcon, ClipboardDocumentCheckIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true }
});

const flattenedData = computed(() => {
  const s = props.submissionData;
  const f = s.formData || {};
  return {
    assessorName: s.assessorName || f.assessorName || 'N/A',
    operativeName: s.operativeName || f.operativeName || 'N/A',
    operativeRole: s.operativeRole || f.operativeRole || 'N/A',
    operativeEmployer: s.operativeEmployer || f.operativeEmployer || 'N/A',
    location: s.location || f.location || 'N/A',
    observationDate: s.observationDate || f.observationDate || null,
    reasonForAssessment: s.reasonForAssessment || f.reasonForAssessment || null,
    visibleSigns: s.visibleSigns || f.visibleSigns || [],
    emotionalSigns: s.emotionalSigns || f.emotionalSigns || [],
    mentalSigns: s.mentalSigns || f.mentalSigns || [],
    workPatternSigns: s.workPatternSigns || f.workPatternSigns || [],
    isFatigueAnIssue: s.isFatigueAnIssue ?? f.isFatigueAnIssue ?? null,
    mitigatingAction: s.mitigatingAction || f.mitigatingAction || null,
    signatureDataUrl: s.signatureDataUrl || f.signatureDataUrl || null
  };
});

const signCategories = computed(() => ({
  'Visible': flattenedData.value.visibleSigns,
  'Emotional': flattenedData.value.emotionalSigns,
  'Mental': flattenedData.value.mentalSigns,
  'Operational': flattenedData.value.workPatternSigns
}));
</script>

<style scoped>
.micro-label { @apply text-[10px] font-black uppercase tracking-[0.2em]; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>